import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  GridApi,
  ValueSetterParams,
} from 'ag-grid-community';
import { toast as message } from 'sonner';
import { queryClient } from '../../utils/constants';
import { useGlobalMetadata } from '../../utils/providers';
import { useFieldEditAccess } from '../../utils/fieldEditAccess';
import { handleOperationError, updateRecord } from '../../utils/supabase-operations';
import { useEditLocks } from '../../hooks/useEditLocks';
import { useEditUndoStack, type EditUndoChange, type EditUndoEntry } from './useEditUndoStack';
import { isValidEmail, trimEmail, validateAndNormalizePhone } from '../../utils/search';

/**
 * Inline editing for a WorkspaceGrid whose rows are records of one CRM table
 * (REG-739): the Submissions list, the Reps list, the Funders list. A column
 * takes part by naming the table field it shows, `context: { editField:
 * 'status' }`, plus `rowField` (a dotted path) when the row keeps the raw
 * column value under another name than the one it displays. Whether it is editable comes from the table metadata (system
 * and calculated fields never are) and the user's capabilities
 * (useFieldEditAccess); the editor comes from the field's type / picklist.
 * Writes go through updateRecord with a compare-and-set on the old value, so
 * RLS, column guards and triggers decide exactly as on the record page.
 *
 * Undo reverses this grid's edits one at a time, newest first, through the
 * same write path (useEditUndoStack). The stack lives for the page visit only.
 * A page can push its own edits onto it (`pushUndo`, e.g. a bulk edit).
 */
export interface TableInlineEditOptions<T> {
  tableName: string;
  columns: ColDef<T>[];
  /** Row field carrying the record id. Default `id`. */
  idField?: string;
  /** TanStack query keys (prefixes) holding this grid's rows; refetched after a write or undo. */
  invalidateQueryKeys?: unknown[][];
}

export interface TableInlineEditGridProps<T> {
  editMode: boolean;
  onCellValueChanged: (event: CellValueChangedEvent<T>) => void;
  onCellEditingStarted: (event: CellEditingStartedEvent<T>) => void;
  onCellEditingStopped: (event: CellEditingStoppedEvent<T>) => void;
}

export interface TableInlineEdit<T> {
  columns: ColDef<T>[];
  editMode: boolean;
  toggleEditMode: () => void;
  /** False when this user may edit none of the columns: hide Edit / Undo. */
  hasEditableColumns: boolean;
  canUndo: boolean;
  nextUndoLabel: string | null;
  undoLast: () => Promise<void>;
  /** Puts another edit of this table's records (a bulk edit) on the same Undo stack. */
  pushUndo: (entry: EditUndoEntry) => void;
  gridProps: TableInlineEditGridProps<T>;
}

interface FieldMeta {
  name?: string;
  ui_label?: string | null;
  data_type?: string | null;
  is_system?: boolean;
  is_calculated?: boolean;
  is_required?: boolean;
  is_email?: boolean;
  is_phone?: boolean;
  relationship?: unknown;
  picklist?: { values?: Array<{ value: string; label?: string; place?: number; is_active?: boolean }> } | null;
}

interface InlineEditContext {
  editField?: string;
  rowField?: string;
}

/** The raw value under `path`; pages keep a missing value as '' for display, the column holds NULL. */
function readPath(data: unknown, path: string): unknown {
  const value = path.split('.').reduce<any>((current, key) => (current == null ? undefined : current[key]), data);
  return value === undefined || value === '' ? null : value;
}

function writePath(data: unknown, path: string, value: unknown): void {
  const keys = path.split('.');
  const parent = keys.slice(0, -1).reduce<any>((current, key) => (current == null ? undefined : current[key]), data);
  if (parent != null) parent[keys[keys.length - 1]] = value;
}

/** Same checks as the record page: a bad email or phone never reaches the database. */
function validationError(field: FieldMeta, label: string, value: unknown): string | null {
  if (value == null || typeof value !== 'string') return null;
  // Older contact columns (rep.email, funder.rep1_phone) are not flagged in metadata.
  const name = field.name ?? '';
  if ((field.is_email || /email$/.test(name)) && !isValidEmail(trimEmail(value))) return `${label}: not a valid email address`;
  if (field.is_phone || /(phone|cell)$/.test(name)) {
    const result = validateAndNormalizePhone(value);
    if (!result.valid) return `${label}: ${result.error}`;
  }
  return null;
}

const EMPTY_QUERY_KEYS: unknown[][] = [];
const EMPTY_FIELDS: Record<string, FieldMeta> = {};
const NUMERIC_TYPES = new Set(['numeric', 'integer', 'bigint', 'smallint', 'double precision', 'real']);
const DATE_TYPES = new Set(['date', 'timestamp with time zone', 'timestamp without time zone']);

function editorFor(field: FieldMeta): Partial<ColDef> | null {
  if (field.relationship) return null;
  const values = field.picklist?.values;
  if (Array.isArray(values) && values.length) {
    const options = [...values]
      .filter((item) => item?.value && item.is_active !== false)
      .sort((a, b) => (a.place ?? 0) - (b.place ?? 0))
      .map((item) => item.value);
    return {
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      cellEditorPopupPosition: 'under',
      cellEditorParams: {
        values: field.is_required ? options : [...options, null],
        formatValue: (value: unknown) => (value == null ? 'Clear' : String(value)),
        valueListMaxHeight: 300,
        searchType: 'matchAny',
        highlightMatch: true,
      },
    };
  }
  const type = field.data_type ?? 'text';
  if (NUMERIC_TYPES.has(type)) return { cellEditor: 'agNumberCellEditor' };
  if (type === 'boolean') return { cellEditor: 'agCheckboxCellEditor' };
  if (DATE_TYPES.has(type)) return { cellEditor: 'agDateStringCellEditor' };
  if (type === 'text' || type === 'character varying') {
    return field.name === 'note'
      ? { cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true, cellEditorParams: { maxLength: 10000, rows: 8, cols: 50 } }
      : { cellEditor: 'agTextCellEditor' };
  }
  return null;
}

/** Editor output -> the value written: blank text clears, numbers parse. */
function normalize(field: FieldMeta, value: unknown): unknown {
  if (value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (NUMERIC_TYPES.has(field.data_type ?? '')) {
      const parsed = Number(trimmed.replace(/[$,%\s]/g, ''));
      return Number.isFinite(parsed) ? parsed : value;
    }
    return trimmed;
  }
  return value;
}

export function useTableInlineEdit<T>({
  tableName,
  columns,
  idField = 'id',
  invalidateQueryKeys = EMPTY_QUERY_KEYS,
}: TableInlineEditOptions<T>): TableInlineEdit<T> {
  const { data: metadata } = useGlobalMetadata();
  const { canEditField } = useFieldEditAccess();
  const fields = useMemo(
    () => ((metadata as any)?.fieldData?.[tableName]?.byFieldName ?? EMPTY_FIELDS) as Record<string, FieldMeta>,
    [metadata, tableName],
  );
  const schema = (metadata as any)?.tableData?.byName?.[tableName]?.schema_name ?? 'public';
  const [editMode, setEditMode] = useState(false);
  const apiRef = useRef<GridApi<T> | null>(null);
  // valueSetter -> onCellValueChanged: the raw value before the edit. The
  // column may show a label (valueGetter), so the event's oldValue can't be written back.
  const pendingOld = useRef(new Map<string, unknown>());
  const {
    getLockOwner,
    acquire: acquireEditLock,
    release: releaseEditLock,
    releaseAll: releaseAllEditLocks,
  } = useEditLocks({ schema, tableName, enabled: editMode });

  useEffect(() => {
    if (!editMode) releaseAllEditLocks();
  }, [editMode, releaseAllEditLocks]);

  const decorated = useMemo(() => {
    let editableCount = 0;
    const next = columns.map((column): ColDef<T> => {
      const context = column.context as InlineEditContext | undefined;
      const editField = context?.editField;
      const rowField = context?.rowField ?? editField ?? '';
      const field = editField ? fields[editField] : undefined;
      if (!editField || !field || field.is_system || field.is_calculated) return column;
      if (!canEditField(tableName, editField)) return column;
      const editor = editorFor({ ...field, name: editField });
      if (!editor) return column;
      editableCount += 1;
      const colId = column.colId ?? column.field ?? editField;
      return {
        ...column,
        ...(editor as Partial<ColDef<T>>),
        cellEditorParams: (params: any) => ({
          ...(typeof editor.cellEditorParams === 'object' ? editor.cellEditorParams : {}),
          value: readPath(params.data, rowField),
        }),
        editable: (params) => editMode
          && (params.data as any)?.[idField] != null
          && !getLockOwner((params.data as any)?.[idField], colId),
        valueSetter: (params: ValueSetterParams<T>) => {
          const before = readPath(params.data, rowField);
          const after = normalize(field, params.newValue);
          if (before === after || (before != null && after != null && String(before) === String(after))) return false;
          pendingOld.current.set(`${params.node?.id}:${editField}`, before);
          writePath(params.data, rowField, after);
          return true;
        },
        cellClassRules: {
          ...(column.cellClassRules ?? {}),
          'live-cell-editing': (params) => Boolean(getLockOwner((params.data as any)?.[idField], colId)),
        },
      };
    });
    return { columns: next, editableCount };
  }, [canEditField, columns, editMode, fields, getLockOwner, idField, tableName]);

  const refetchRows = useCallback(async () => {
    await Promise.all(invalidateQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  }, [invalidateQueryKeys]);

  const write = useCallback(async (recordId: number | string, field: string, value: unknown, expected: unknown) => {
    const { error } = await updateRecord({
      tableName,
      schema,
      data: { [field]: value },
      whereClause: { id: recordId },
      options: { fieldName: field, expectedValues: { [field]: expected } },
    });
    if (error) throw error;
  }, [schema, tableName]);

  const setRowValue = useCallback((rowNodeId: string | undefined, rowField: string, value: unknown) => {
    const node = rowNodeId ? apiRef.current?.getRowNode(rowNodeId) : undefined;
    if (!node?.data) return;
    writePath(node.data, rowField, value);
    apiRef.current?.refreshCells({ rowNodes: [node], force: true });
  }, []);

  // Undo patches the reverted rows in place, then refetches.
  const handleUndone = useCallback((entry: EditUndoEntry, reverted: EditUndoChange[]) => {
    for (const change of reverted) setRowValue(change.rowNodeId, entry.rowField ?? entry.field, change.oldValue);
    void refetchRows();
  }, [refetchRows, setRowValue]);
  const undo = useEditUndoStack({ onUndone: handleUndone });

  const onCellValueChanged = useCallback(async (event: CellValueChangedEvent<T>) => {
    apiRef.current = event.api;
    const context = event.colDef.context as InlineEditContext | undefined;
    const editField = context?.editField;
    if (!editField) return;
    const rowField = context?.rowField ?? editField;
    const key = `${event.node.id}:${editField}`;
    if (!pendingOld.current.has(key)) return;
    const oldValue = pendingOld.current.get(key);
    pendingOld.current.delete(key);
    const newValue = readPath(event.data, rowField);
    const recordId = (event.data as any)?.[idField];
    const label = event.colDef.headerName || editField;
    try {
      if (recordId == null) throw new Error('This row has no record to update');
      const invalid = validationError({ ...fields[editField], name: editField }, label, newValue);
      if (invalid) throw new Error(invalid);
      await write(recordId, editField, newValue, oldValue);
      undo.push({
        tableName,
        schema,
        field: editField,
        rowField,
        newValue,
        label,
        changes: [{ recordId, oldValue, rowNodeId: event.node.id }],
      });
      message.success('Saved');
      void refetchRows();
    } catch (error) {
      setRowValue(event.node.id, rowField, oldValue);
      message.error(handleOperationError(error, 'Could not save this change').userMessage);
    }
  }, [fields, idField, refetchRows, schema, setRowValue, tableName, undo, write]);

  const onCellEditingStarted = useCallback((event: CellEditingStartedEvent<T>) => {
    apiRef.current = event.api;
    const rowId = (event.data as any)?.[idField];
    if (rowId != null) acquireEditLock(rowId, event.column.getColId());
  }, [acquireEditLock, idField]);

  const onCellEditingStopped = useCallback((event: CellEditingStoppedEvent<T>) => {
    const rowId = (event.data as any)?.[idField];
    if (rowId != null) releaseEditLock(rowId, event.column.getColId());
  }, [idField, releaseEditLock]);

  const toggleEditMode = useCallback(() => setEditMode((current) => !current), []);

  return useMemo(() => ({
    columns: decorated.columns,
    editMode,
    toggleEditMode,
    hasEditableColumns: decorated.editableCount > 0,
    canUndo: undo.canUndo,
    nextUndoLabel: undo.nextUndoLabel,
    undoLast: undo.undoLast,
    pushUndo: undo.push,
    gridProps: { editMode, onCellValueChanged, onCellEditingStarted, onCellEditingStopped },
  }), [decorated, editMode, onCellEditingStarted, onCellEditingStopped, onCellValueChanged, toggleEditMode, undo]);
}
